<script setup lang="ts">
import { useSlots, computed } from 'vue'
import { navigateTo } from '@@/bits/location'

const props = withDefaults(
  defineProps<{
    // Determines if button is disabled
    disabled?: boolean
    //  Determines the button's main style theme
    variation?: string
    //  Determines the color of button
    colorScheme?: 'default' | 'black' | 'white' | 'green' | 'orange' | 'grey'
    // Allows clicking the button navigate to a URL
    href?: string
    //  Determines where the url should be opened in
    hrefTarget?: string
    //  Determines if text-label should have margin
    xTextMargin?: boolean
  }>(),
  {
    disabled: false,
    variation: undefined,
    colorScheme: 'default',
    href: undefined,
    hrefTarget: '_self',
    xTextMargin: true,
  },
)

const slots = useSlots()

const emit = defineEmits<{
  (name: 'click', event: MouseEvent): void
}>()

const hasText = computed(() => !!slots.default && slots.default().toString().trim() !== '')

const hasIcon = computed(() => !!slots.icon)

const variationClass = computed(() => {
  switch (props.variation) {
    case 'contained':
    case 'flat':
      return 'flat-button'
    case 'text':
      return 'text-button'
    case 'fat-contained':
      return 'fat-contained-button'
    case 'small-contained':
      return 'small-contained-button'
    case 'large-contained':
      return 'large-contained-button'
    case 'fat-text':
      return 'fat-text-button'
    case 'fat-text-lower':
      return 'fat-text-button lower'
    case 'bordered-text':
      return 'fat-text-button bordered-text-button'
    case 'flat-bordered':
      return 'flat-button bordered-flat-button'
    default:
      return 'text-button'
  }
})

const iconClass = computed(() => {
  const iconClassesArray: string[] = []
  if (hasIcon.value) {
    iconClassesArray.push('icon-button')
  }
  if (!hasText.value) {
    iconClassesArray.push('button-without-text')
  }
  if (props.variation === 'bordered-icon') {
    iconClassesArray.push('bordered-icon-button')
  }
  return iconClassesArray.join(' ')
})

const colorSchemeClass = computed(() => props.colorScheme + '-color')

const classNames = computed(() => [
  'button',
  !(hasIcon.value && !hasText.value) ? variationClass.value : '',
  colorSchemeClass.value,
  iconClass.value,
  props.xTextMargin ? '' : 'no-text-margin',
])

function click(event): void {
  if (props.href) {
    navigateTo(props.href, { target: props.hrefTarget })
  }
  emit('click', event)
}
</script>

<template>
  <!--
    triggered on click
    @event click
  -->
  <button :class="classNames" :disabled="disabled" v-bind="$props" @click="click">
    <slot name="icon"></slot>
    <div v-if="hasText" class="text-label"><slot /></div>
  </button>
</template>

<style lang="scss" scoped>
@import '@@/styles/global';
@import '@@/styles/3/modules/_shadow.scss';
@import '@@/styles/3/modules/_button.scss';
@import '@@/styles/3/modules/_button_colors.scss';
@import '@@/styles/3/modules/_utilities.scss';
@import '@@/styles/3/modules/_responsive.scss';
@import '@@/styles/3/partials/fonts/_material_icons.scss';

.button {
  &[disabled],
  &:disabled {
    opacity: 0.26;
    cursor: not-allowed;
  }
}

@mixin fat-button {
  font-family: Inter, Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', Ubuntu, 'Helvetica Neue', sans-serif;
  font-size: 16px;
  box-sizing: border-box;
  font-weight: 600;
  border: none;
  border-radius: 16px;
  padding: 0px 24px;
  line-height: 42px;
  transition: all 0.2s;
  cursor: pointer;
  text-decoration: none;
  text-align: center;
  touch-action: manipulation;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  // @include shadow-z2;
  &[disabled],
  &:disabled {
    opacity: 0.26;
    background: none;
  }
}
.icon-button {
  display: inline-flex;
  justify-content: center;
  align-items: center;

  &[disabled],
  &:disabled {
    transform: none;
    box-shadow: none;
  }
}
.text-label {
  margin: 0 16px;
  .no-text-margin & {
    margin: 0;
  }
}
$grey-button-color: #8b8b8b;
$white-button-color: #fff;
$black-button-color: #0f0f0f;
$grey-800-button-color: #404040;
$mid-grey-button-color: #646464;
$light-grey-button-color: rgba(255, 255, 255, 0.7);
$purple-button-color: #be66e8;
$orange-button-color: #ff843c;
$green-button-color: #3bb21e;
$offwhite-button-color: #f5f5f5;
.fat-contained-button {
  @include fat-button;
  @include hover {
    transform: translateY(1px);
    &[disabled],
    &:disabled {
      transform: none;
      box-shadow: none;
    }
  }
  @include focus {
    transform: translateY(2px);
  }
  @include active {
    transform: translateY(2px);
  }
  @include button-colors;
}
.large-contained-button {
  @include fat-button;
  font-size: 20px;
  font-weight: 600;
  padding: 0px 28px;
  border-radius: 20px;
  letter-spacing: 0.5px;
  @include hover {
    transform: translateY(1px);
  }
  @include focus {
    transform: translateY(2px);
  }
  @include active {
    transform: translateY(2px);
  }
  @include button-colors;
}

.small-contained-button {
  @include fat-button;
  font-size: 14px;
  font-weight: 600;
  line-height: 32px;
  border-radius: 12px;
  padding: 0px 20px;
  @include hover {
    transform: translateY(1px);
  }
  @include focus {
    transform: translateY(2px);
  }
  @include active {
    transform: translateY(2px);
  }
  @include button-colors;
}

.fat-text-button {
  @include fat-button;
  text-transform: uppercase;
  box-shadow: none;
  background: none;
  padding: 0 8px;
  &.default-color {
    @include text-button-color($button-color);
  }
  &.black-color {
    @include text-button-color($white-color);
  }
  &.white-color {
    @include text-button-color($white-button-color);
  }
  &.grey-color {
    @include text-button-color($mid-grey-button-color);
    .dark-mode & {
      @include text-button-color($light-grey-button-color);
    }
  }
  &.lower {
    text-transform: none;
  }
  &.bordered-text-button {
    text-transform: none;
    font-weight: normal;
    font-size: 14px;
    line-height: 40px;
    padding: 0;
    .text-label {
      margin: 0;
    }
    color: $body-text-color;
    border-radius: 16px;
    @include inverse-button-color($offwhite-button-color);
    @include button-hover-active {
      @apply bg-grape-500 dark:bg-canary-500;
      @apply text-white-100 dark:text-highway-100;
      font-weight: 600;
    }
    &.selected {
      @apply bg-grape-500 dark:bg-canary-500;
      @apply text-white-100 dark:text-highway-100;
      font-weight: 600;
    }
    .dark-mode & {
      @apply text-white-100;
      @include inverse-button-color($grey-800-button-color);
      @include button-hover-active {
        @apply bg-canary-500 text-highway-100;
        font-weight: 600;
      }
      &.selected {
        @apply bg-canary-500 text-highway-100;
        font-weight: 600;
      }
    }
  }
}
.icon-button {
  * {
    transition: all 0.2s;
  }
  &.default-color {
    i {
      @include button-hover-active {
        color: $text-color;
      }
    }
  }
  &.white-color {
    i {
      @include text-button-color($grey-button-color);
    }
  }
  &.black-color {
    i {
      @include button-hover-active {
        color: $text-color;
      }
    }
  }
  &.bordered-icon-button {
    border-radius: 16px;
    width: 40px;
    height: 40px;
    @include inverse-button-color($offwhite-button-color);
    @include button-hover-active {
      @apply bg-grape-500 dark:bg-canary-500;
      i {
        @apply text-white-100 dark:text-highway-100;
      }
    }
    .dark-mode & {
      @include inverse-button-color($grey-800-button-color);
      @include button-hover-active {
        @apply bg-canary-500;
        i {
          @apply text-highway-100;
        }
      }
    }
  }
}
.button-without-text {
  height: 42px;
  padding: 0;
}
.bordered-flat-button {
  border-radius: 16px;
  height: 40px;
  text-transform: none;
  padding: 0px 24px;
  font-size: 16px;
  .text-label {
    margin: 0px;
  }
}

/**
 * Styling for base_button_list
*/

@mixin verticalButtonListMember {
  width: 100%;
  justify-content: flex-start;
  margin: 0;
  border-radius: 0;
  line-height: 64px;
  box-shadow: none !important;
  @include hover {
    transform: none;
  }
  &:first-of-type {
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
  }
  &:last-of-type {
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  }
  &:not(:first-of-type) {
    margin-top: 1px;
    border-top: 1px solid transparent;
  }
  .text-label {
    font-weight: 600;
  }
  &:after {
    content: 'keyboard_arrow_right';
    @include immaterial-icon;
    position: absolute;
    right: 16px;
    color: $inverse-caption-text-color;
    display: none;
  }
  &.icon-button {
    i {
      margin-left: -8px;
      padding: 6px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.24);
    }
    &.black-color {
      i {
        background: lighten($black-button-color, 5%);
      }
    }
    &.white-color {
      i {
        background: rgba(0, 0, 0, 0.1);
      }
    }
    &:after {
      display: block;
    }
  }
  position: relative;

  &.white-color {
    &:after {
      color: $caption-text-color;
    }
  }
}

.button-list {
  .button {
    margin: 8px;
  }
  &.vertical {
    .button {
      @include verticalButtonListMember;
    }
  }
  @include respondToPhone {
    .button {
      @include verticalButtonListMember;
    }
  }
}
</style>
